/**
 * `QueryBuilder` — the public entry point.
 *
 * Pipeline: validate input shape → normalize → build QueryPlan → validate plan
 * (semantic + engine) → dispatch to the engine compiler. Engines are resolved
 * from `transpile({ engine })` or the constructor; compilers come from the
 * registry. Adding an engine does not change this class.
 */
import { registerDefaultCompilers } from './compilers/register-default-compilers';
import { defaultRegistry } from './compilers/compiler-registry';
import type {
    MutationTranspileResult,
    TranspileOptions,
    TranspileResult,
} from './compilers/compiler';
import { QueryBuilderError } from './errors/query-builder-error';
import type { Engine, MutationInput, QueryInput } from './input/input-types';
import { normalizeInput } from './input/normalize-input';
import { validateInputShape } from './input/validate-input';
import { validateMutationInput } from './input/validate-mutation';
import { inputToPlan } from './plan/input-to-plan';
import { inputToMutationPlan } from './plan/input-to-mutation-plan';
import type { MutationPlan } from './plan/mutation-plan';
import type { QueryPlan } from './plan/query-plan';
import { validatePlan } from './plan/validate-plan';

registerDefaultCompilers(defaultRegistry);

export type QueryBuilderOptions = {
    engine?: Engine;
};

export type ValidationResult = {
    valid: boolean;
    errors: QueryBuilderError[];
};

export class QueryBuilder {
    constructor(
        private readonly input: QueryInput,
        private readonly options: QueryBuilderOptions = {},
    ) {}

    /** Validate input + plan for the resolved engine without throwing. */
    validate(options: TranspileOptions = {}): ValidationResult {
        const engine = resolveEngine(options.engine, this.options.engine);
        const shapeErrors = validateInputShape(this.input);
        if (shapeErrors.length > 0) {
            return { valid: false, errors: shapeErrors };
        }
        const plan = inputToPlan(normalizeInput(this.input));
        const planErrors = validatePlan(plan, { engine });
        return { valid: planErrors.length === 0, errors: planErrors };
    }

    /** Return the validated {@link QueryPlan}. Throws on the first error. */
    plan(options: TranspileOptions = {}): QueryPlan {
        const engine = resolveEngine(options.engine, this.options.engine);
        this.assertValid(validateInputShape(this.input));
        const plan = inputToPlan(normalizeInput(this.input));
        this.assertValid(validatePlan(plan, { engine }));
        return plan;
    }

    /** Compile the query for the resolved engine. Throws on validation errors. */
    transpile(options: TranspileOptions = {}): TranspileResult {
        const engine = resolveEngine(options.engine, this.options.engine);
        const plan = this.plan(options);
        return defaultRegistry.get(engine).compile(plan);
    }

    private assertValid(errors: QueryBuilderError[]): void {
        if (errors.length > 0) {
            throw errors[0];
        }
    }
}

function resolveEngine(
    optionEngine: Engine | undefined,
    ctorEngine: Engine | undefined,
): Engine {
    const engine = optionEngine ?? ctorEngine;
    if (!engine) {
        throw new QueryBuilderError({
            code: 'MISSING_ENGINE',
            path: '$.engine',
            message:
                'Engine must be provided in the constructor or transpile options.',
        });
    }
    return engine;
}

/**
 * `MutationBuilder` — portable write mutations. Mirrors {@link QueryBuilder} but
 * compiles a {@link MutationInput} into an INSERT/UPSERT/UPDATE/DELETE (SQL) or
 * a bulk index / `_delete_by_query` / `_update_by_query` (ES/OS) via the engine
 * compiler's `compileMutation`.
 */
export class MutationBuilder {
    constructor(
        private readonly input: MutationInput,
        private readonly options: QueryBuilderOptions = {},
    ) {}

    /** Validate the mutation input without throwing. */
    validate(): ValidationResult {
        const errors = validateMutationInput(this.input);
        return { valid: errors.length === 0, errors };
    }

    /** Return the validated {@link MutationPlan}. Throws on the first error. */
    plan(): MutationPlan {
        const errors = validateMutationInput(this.input);
        if (errors.length > 0) {
            throw errors[0];
        }
        return inputToMutationPlan(this.input);
    }

    /** Compile the mutation for the resolved engine. Throws on validation errors. */
    transpile(options: TranspileOptions = {}): MutationTranspileResult {
        const engine = resolveEngine(options.engine, this.options.engine);
        const plan = this.plan();
        const compiler = defaultRegistry.get(engine);
        if (!compiler.compileMutation) {
            throw new QueryBuilderError({
                code: 'UNSUPPORTED_ENGINE_BEHAVIOR',
                path: '$.engine',
                message: `Engine "${engine}" does not support mutations.`,
                details: { engine },
            });
        }
        return compiler.compileMutation(plan);
    }
}
